import React, { useEffect, useState, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/scrollbar';
import { FreeMode, Scrollbar, Mousewheel, Autoplay } from 'swiper/modules';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import useDeviceSize from '@/hooks/useDeviceSize';
import { Pagination } from 'swiper/modules';
import SwiperClass from 'swiper/types/swiper-class';
import type { SwiperModule } from 'swiper/types';

interface Props {
  list?: React.ReactNode[];
  isMargin?: boolean;
  freeMode?: boolean;
  scrollbarHide?: boolean;
  slidesPerView?: number | 'auto';
  spaceBetween?: number;
  type: string;
  category: string;
  onSwiper: (swiper: SwiperClass) => void;
  onSwiperIndex: (index: number) => void;
  enableAutoplay?: boolean;
  autoplayDelay?: number;
}

const SwiperBox = ({
  list = [],
  isMargin = true,
  freeMode = false,
  scrollbarHide,
  slidesPerView = 'auto',
  type,
  category,
  onSwiper,
  onSwiperIndex,
  enableAutoplay = false,
  autoplayDelay = 3000,
}: Props) => {
  const [swiperIndex, setSwiperIndex] = useState(0);
  const [swiperInstance, setSwiperInstance] = useState<SwiperClass | null>(null);
  const [contentHeight, setContentHeight] = useState('auto');
  const slidesRef = useRef<(HTMLElement | null)[]>([]);
  const isDesktop = useDeviceSize();

  // Reset refs array when list changes
  useEffect(() => {
    slidesRef.current = slidesRef.current.slice(0, list.length);
  }, [list]);

  const modules = (): SwiperModule[] => {
    const baseModules = slidesPerView 
      ? [Scrollbar, FreeMode, Mousewheel]
      : [Scrollbar, Mousewheel, Pagination];
    
    // Autoplay 모듈 추가 (enableAutoplay가 true이거나 type이 'B'일 때)
    if (enableAutoplay || type === 'B') {
      baseModules.push(Autoplay);
    }
    
    return baseModules;
  };

  const handleSwiperIndex = (data: number) => {
    setSwiperIndex(data);
    onSwiperIndex(data);
  };

  const pagination = {
    clickable: true,
    renderBullet: function (index: number, className: string) {
      return '<span class="' + className + '">' + (index + 1) + '</span>';
    },
  };

  // Update swiper height when content changes
  const updateHeight = () => {
    if (!swiperInstance) return;

    // Find the tallest slide
    const heights = slidesRef.current
      .filter((ref): ref is HTMLElement => ref !== null)
      .map(ref => ref.clientHeight);

    if (heights.length > 0) {
      const maxHeight = Math.max(...heights);
      if (maxHeight > 0) {
        setContentHeight(`${maxHeight}px`);
        swiperInstance.updateSize();
      }
    }
  };

  // Handle resize and content changes
  useEffect(() => {
    const handleResize = () => {
      updateHeight();
    };

    window.addEventListener('resize', handleResize);

    // Initial height update
    setTimeout(updateHeight, 100);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [swiperInstance, list, isDesktop]);

  // Re-calculate height when slides might have changed content
  useEffect(() => {
    updateHeight();
  }, [list, category, isDesktop]);

  // Start autoplay when swiper instance is ready
  useEffect(() => {
    if (swiperInstance && (enableAutoplay || type === 'B')) {
      // 약간의 지연 후 autoplay 시작
      setTimeout(() => {
        if (swiperInstance.autoplay) {
          swiperInstance.autoplay.start();
        }
      }, 100);
    }
  }, [swiperInstance, enableAutoplay, type]);

  return (
    <div className="h-auto w-full py-2">
      <Swiper
        direction={'horizontal'}
        slidesPerView={slidesPerView}
        spaceBetween={slidesPerView ? 40 : undefined}
        loop={type === 'B'}
        touchRatio={0.5}
        allowTouchMove={true}
        allowSlideNext={true}
        allowSlidePrev={true}
        style={{
          height: contentHeight,
          transition: 'height 0.3s ease',
          paddingTop: '15px',
          paddingBottom: '50px',
        }}
        pagination={pagination}
        scrollbar={
          type === 'A'
            ? {
                draggable: true,
                dragSize: 'auto',
                horizontalClass: 'swiper-scrollbar',
              }
            : false
        }
        onActiveIndexChange={(e) => handleSwiperIndex(e.realIndex)}
        onSwiper={(e) => {
          setSwiperInstance(e);
          onSwiper(e);
        }}
        onInit={(swiper) => {
          // 초기화 후 지연 시간을 두고 autoplay 시작
          if (enableAutoplay || type === 'B') {
            setTimeout(() => {
              if (swiper.autoplay) {
                swiper.autoplay.start();
              }
            }, enableAutoplay ? autoplayDelay : 3000); // 첫 슬라이드는 설정된 딜레이만큼 보여줌
          }
        }}
        draggable={type === 'A'}
        autoplay={enableAutoplay ? {
          delay: autoplayDelay,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
          stopOnLastSlide: false,
          waitForTransition: true,
        } : type === 'B' ? {
          delay: 3000,
          disableOnInteraction: false,
          stopOnLastSlide: false,
          waitForTransition: true,
        } : false}
        freeMode={freeMode}
        modules={modules()}
        className={`${isMargin ? 'swiper-margin' : ''} ${scrollbarHide ? '' : 'hide-scrollbar'}`}
        observer={true}
        observeParents={true}
      >
        {list?.map((item, index) => (
          <SwiperSlide
            key={index}
            className={`${category === 'feature' ? 'mobile:min-w-[250px]' : ''}`}
          >
            {item}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default SwiperBox;